import React from 'react';
import { ChoiceGroup, IChoiceGroupOption, Checkbox, TextField } from '@fluentui/react';
import {
    IMPORTANCE_REASON_LABELS,
    ImportanceReason,
} from 'app/modules/user/createTicket/customerImpact/types';
import { ISelectedData } from 'app/models/common/response';

interface IProps {
    item: ISelectedData;
    /** Generic change callback. The parent adapts this to its own event-based handler. */
    onFieldChange: (type: string, value: string) => void;
    /** Show the opportunity-only "Entire deal depends on this issue" checkbox. */
    showEntireDeal?: boolean;
    /** Show the "same issue as the ticket" checkbox. Defaults to true. */
    showSameIssue?: boolean;
}

const reasonOptions: IChoiceGroupOption[] = (Object.keys(IMPORTANCE_REASON_LABELS) as ImportanceReason[]).map((key) => ({
    key,
    text: IMPORTANCE_REASON_LABELS[key].label,
    onRenderField: (fieldProps, defaultRender) => (
        <div className="reason-option">
            {defaultRender?.(fieldProps)}
            {IMPORTANCE_REASON_LABELS[key].description && (
                <span className="reason-desc">{IMPORTANCE_REASON_LABELS[key].description}</span>
            )}
        </div>
    ),
}));

/**
 * The importance reason is required. It is complete when a reason is selected and,
 * for the "Other" option, the free-text detail is non-blank. Mirrors the
 * create-ticket impact panels so the add-customer submit paths gate the same way.
 */
export const isImpactReasonComplete = (item?: ISelectedData): boolean => {
    if (!item?.importanceReason) {
        return false;
    }
    if (item.importanceReason === 'other') {
        return !!item.importanceOtherText?.trim();
    }
    return true;
};

/**
 * Impact fields shared by the customer, opportunity, and manual add-customer
 * forms: "Why is this customer important?" radios (+ Other text), the "same issue"
 * checkbox, and an optional opportunity-only "Entire deal depends" checkbox. The
 * selected values are folded into the free-text customerImpactDescription at submit
 * (see buildAddCustomerDescription in AddCustomer.tsx), matching the create-ticket flow.
 */
const ImpactReasonFields: React.FC<IProps> = ({ item, onFieldChange, showEntireDeal, showSameIssue = true }) => {
    return (
        <div className="impact-reason-fields">
            {showEntireDeal && (
                <div className="impact-reason-checkbox">
                    <Checkbox
                        label="Entire deal depends on this issue"
                        data-testid="impact-entire-deal-checkbox"
                        checked={!!item.entireDealDepends}
                        onChange={(_e, checked) => onFieldChange('entire_deal', checked ? 'true' : 'false')}
                    />
                </div>
            )}
            <label className="label-styles">
                Why is this customer important?<span className="required">*</span>
            </label>
            <ChoiceGroup
                data-testid="impact-importance-reason-group"
                selectedKey={item.importanceReason}
                options={reasonOptions}
                onChange={(_e, option?: IChoiceGroupOption) => onFieldChange('importance_reason', option?.key ?? '')}
            />
            {item.importanceReason === 'other' && (
                <TextField
                    data-testid="impact-importance-other-input"
                    ariaLabel="Describe why this customer is important"
                    placeholder="e.g., Key partner in healthcare vertical"
                    value={item.importanceOtherText ?? ''}
                    onChange={(e) => onFieldChange('importance_other', (e.target as HTMLInputElement).value)}
                />
            )}
            {showSameIssue && (
                <div className="impact-reason-checkbox">
                    <Checkbox
                        label="This customer also has the same issue as the ticket"
                        data-testid="impact-same-issue-checkbox"
                        checked={!!item.sameIssue}
                        onChange={(_e, checked) => onFieldChange('same_issue', checked ? 'true' : 'false')}
                    />
                </div>
            )}
        </div>
    );
};

export default ImpactReasonFields;
